import React, { useState, useEffect } from 'react';
import './CommonStyles.css';
import './Products.css';
import './AllSellStyles.css';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import AddStockAdjustment from './AddStockAdjustment';
import ViewStockAdjustmentModal from './ViewStockAdjustmentModal';
import apiService from '../../services/api';
import useToast from '../../hooks/useToast';

const StockAdjustment = ({ activeTab: propActiveTab }) => {
    const [activeTab, setActiveTab] = useState('list_stock_adjustment');
    const { showSuccess, showError } = useToast();

    useEffect(() => {
        if (propActiveTab) {
            setActiveTab(propActiveTab === 'stock-adjustment' ? 'list_stock_adjustment' : propActiveTab);
        }
    }, [propActiveTab]);

    const [adjustments, setAdjustments]     = useState([]);
    const [isLoading, setIsLoading]         = useState(false);
    const [selectedAdjustment, setSelectedAdjustment] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);

    const [searchTerm, setSearchTerm]       = useState('');
    const [perPage, setPerPage]             = useState(25);
    const [currentPage, setCurrentPage]     = useState(1);
    const [totalAdjustments, setTotalAdjustments] = useState(0);
    const [lastPage, setLastPage]           = useState(1);
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const [showFilters, setShowFilters]     = useState(false);

    // Delete state
    const [deleteId, setDeleteId]           = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting]       = useState(false);

    const [filters, setFilters] = useState({
        location_id: 'all',
        adjustment_type: 'all',
        start_date: '',
        end_date: ''
    });
    const [locations, setLocations] = useState([]);

    useEffect(() => {
        apiService.getBusinessLocations()
            .then(r => setLocations(r.data || []))
            .catch(() => {});
    }, []);

    const fetchAdjustments = async (page = currentPage, search = searchTerm) => {
        setIsLoading(true);
        try {
            const params = { page, per_page: perPage, search };
            if (filters.location_id !== 'all')    params.location_id     = filters.location_id;
            if (filters.adjustment_type !== 'all') params.adjustment_type = filters.adjustment_type;
            if (filters.start_date)               params.start_date      = filters.start_date;
            if (filters.end_date)                 params.end_date        = filters.end_date;

            const response = await apiService.getStockAdjustments(params);
            setAdjustments(response.data || []);
            setTotalAdjustments(response.total || 0);
            setLastPage(response.last_page || 1);
        } catch (err) {
            console.error('Failed to fetch adjustments', err);
            showError('Failed to load stock adjustments.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'list_stock_adjustment') fetchAdjustments(currentPage, searchTerm);
    }, [activeTab, currentPage, perPage]);

    // Debounced search
    useEffect(() => {
        const t = setTimeout(() => {
            if (activeTab === 'list_stock_adjustment') {
                setCurrentPage(1);
                fetchAdjustments(1, searchTerm);
            }
        }, 400);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const handleApplyFilters = () => {
        setCurrentPage(1);
        fetchAdjustments(1, searchTerm);
        setShowFilters(false);
    };

    const handleResetFilters = () => {
        const reset = { location_id: 'all', adjustment_type: 'all', start_date: '', end_date: '' };
        setFilters(reset);
        setCurrentPage(1);
        setShowFilters(false);
        setTimeout(() => fetchAdjustments(1, searchTerm), 0);
    };

    // ── Delete ────────────────────────────────────────────────────────────────
    const handleDeleteClick = (id) => {
        setDeleteId(id);
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        setIsDeleting(true);
        try {
            await apiService.deleteStockAdjustment(deleteId);
            showSuccess('Stock adjustment deleted successfully.');
            setShowDeleteConfirm(false);
            setDeleteId(null);
            fetchAdjustments(currentPage, searchTerm);
        } catch (err) {
            showError(err.message || 'Failed to delete stock adjustment.');
        } finally {
            setIsDeleting(false);
        }
    };

    if (activeTab === 'add_stock_adjustment') {
        return <AddStockAdjustment onBack={() => setActiveTab('list_stock_adjustment')} onSaveSuccess={() => {
            setActiveTab('list_stock_adjustment');
            fetchAdjustments(1, '');
            setSearchTerm('');
            setCurrentPage(1);
        }} />;
    }

    const handleExport = (format) => {
        if (!adjustments.length) { showError('No data to export'); setShowExportDropdown(false); return; }
        const headers = ['Date', 'Reference No', 'Location', 'Adjustment type', 'Total Amount', 'Total amount recovered', 'Reason', 'Added By'];
        const rows = adjustments.map(adj => [
            adj.transaction_date || '',
            adj.ref_no || '',
            (typeof adj.location_name === 'string' ? adj.location_name : adj.location?.name) || '',
            adj.adjustment_type || '',
            parseFloat(adj.final_total || 0).toFixed(2),
            parseFloat(adj.total_amount_recovered || 0).toFixed(2),
            adj.additional_notes || '',
            adj.added_by || 'N/A',
        ]);
        if (format === 'CSV')   exportToCSV(headers, rows, 'stock_adjustments');
        else if (format === 'Excel') exportToExcel(headers, rows, 'stock_adjustments');
        else if (format === 'PDF')   exportToPDF('Stock Adjustment List', headers, rows, 'stock_adjustments');
        else if (format === 'Print') printPage();
        setShowExportDropdown(false);
    };

    const fromEntry = totalAdjustments === 0 ? 0 : (currentPage - 1) * perPage + 1;
    const toEntry   = Math.min(currentPage * perPage, totalAdjustments);

    return (
        <div className="products-container">
            <div className="main-card">
                <div className="table-controls">
                    <div className="table-controls-left">
                        <div className="entries-dropdown">
                            <span>Show</span>
                            <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                            <span>entries</span>
                        </div>
                    </div>
                    <div className="table-controls-right">
                        <div className="tabs-search-wrapper">
                            <input type="text" placeholder="Search..." value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)} className="tabs-search-input" />
                            <button className="filter-toggle-btn" onClick={() => setShowFilters(true)} title="Open Filters">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                                </svg>
                                Filters
                            </button>
                        </div>
                        <div className="export-dropdown-container" style={{ position: 'relative' }}>
                            <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                    <polyline points="7 10 12 15 17 10"></polyline>
                                    <line x1="12" y1="15" x2="12" y2="3"></line>
                                </svg>
                                Export ▾
                            </button>
                            {showExportDropdown && (
                                <div className="export-dropdown-menu">
                                    <button onClick={() => handleExport('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                                    <button onClick={() => handleExport('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                                    <button onClick={() => handleExport('Print')}><i className="fa fa-print"></i> Print</button>
                                    <button onClick={() => handleExport('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                                </div>
                            )}
                        </div>
                        <button className="control-btn-primary" onClick={() => setActiveTab('add_stock_adjustment')}>
                            <span className="plus-icon">+</span> Add
                        </button>
                    </div>
                </div>

                <div className="products-table-container">
                    {isLoading ? (
                        <div className="loading-sales">
                            <h3>⏳ Loading Stock Adjustments...</h3>
                            <p>Fetching stock adjustment data from server...</p>
                        </div>
                    ) : adjustments.length === 0 ? (
                        <div className="no-sales">
                            <h3>📊 No Stock Adjustments Found</h3>
                            <p>No stock adjustments have been recorded yet.</p>
                        </div>
                    ) : (
                        <table className="products-table">
                            <thead>
                                <tr>
                                    <th>Action</th>
                                    <th>Date</th>
                                    <th>Reference No</th>
                                    <th>Location</th>
                                    <th>Adjustment type</th>
                                    <th>Total Amount</th>
                                    <th>Total amount recovered</th>
                                    <th>Reason</th>
                                    <th>Added By</th>
                                </tr>
                            </thead>
                            <tbody>
                                {adjustments.map((adj) => (
                                    <tr key={adj.id}>
                                        <td>
                                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                                                <button onClick={() => { setSelectedAdjustment(adj); setShowViewModal(true); }}
                                                    style={{ padding: '0.25rem 0.6rem', background: '#fff', border: '1px solid #8b5cf6', color: '#8b5cf6', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer' }}>
                                                    <i className="fa fa-eye"></i> View
                                                </button>
                                                <button onClick={() => handleDeleteClick(adj.id)}
                                                    style={{ padding: '0.25rem 0.6rem', background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer' }}>
                                                    <i className="fa fa-trash"></i> Delete
                                                </button>
                                            </div>
                                        </td>
                                        <td>{adj.transaction_date ? adj.transaction_date.slice(0, 16).replace('T', ' ') : '-'}</td>
                                        <td style={{ fontWeight: '700' }}>{adj.ref_no || '-'}</td>
                                        <td>{typeof adj.location_name === 'string' ? adj.location_name : (adj.location?.name || '-')}</td>
                                        <td>
                                            <span style={{
                                                padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: '800',
                                                background: adj.adjustment_type === 'abnormal' ? '#fee2e2' : '#f0fdf4',
                                                color: adj.adjustment_type === 'abnormal' ? '#b91c1c' : '#15803d',
                                                textTransform: 'uppercase'
                                            }}>
                                                {adj.adjustment_type || '-'}
                                            </span>
                                        </td>
                                        <td style={{ fontWeight: '700' }}>${parseFloat(adj.final_total || 0).toFixed(2)}</td>
                                        <td>${parseFloat(adj.total_amount_recovered || 0).toFixed(2)}</td>
                                        <td style={{ fontStyle: 'italic' }}>{adj.additional_notes || '--'}</td>
                                        <td>{adj.added_by || 'N/A'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                        Showing {fromEntry} to {toEntry} of {totalAdjustments} entries
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button className="pagination-btn" disabled={currentPage <= 1}
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>Previous</button>
                        <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', fontSize: '0.875rem' }}>
                            Page {currentPage} of {lastPage}
                        </span>
                        <button className="pagination-btn" disabled={currentPage >= lastPage}
                            onClick={() => setCurrentPage(p => Math.min(lastPage, p + 1))}>Next</button>
                    </div>
                </div>
            </div>

            {/* View Modal */}
            {showViewModal && (
                <ViewStockAdjustmentModal
                    adjustment={selectedAdjustment}
                    onClose={() => setShowViewModal(false)}
                />
            )}

            {/* Delete Confirm Modal */}
            {showDeleteConfirm && (
                <div className="modal-overlay" onClick={() => !isDeleting && setShowDeleteConfirm(false)}>
                    <div className="modal-container" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px', width: '95%' }}>
                        <div className="modal-header">
                            <h2>Delete Stock Adjustment</h2>
                            <button className="modal-close-btn" onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}>✕</button>
                        </div>
                        <div style={{ padding: '1.5rem', color: '#475569' }}>
                            Are you sure you want to delete this stock adjustment? This will restore the product quantities. This action cannot be undone.
                        </div>
                        <div className="modal-footer">
                            <button className="modal-btn close-btn" onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}>Cancel</button>
                            <button onClick={handleDeleteConfirm} disabled={isDeleting}
                                style={{ padding: '0.5rem 1.5rem', background: isDeleting ? '#fca5a5' : '#dc2626', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '700', cursor: isDeleting ? 'not-allowed' : 'pointer' }}>
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters Modal */}
            {showFilters && (
                <div className="modal-overlay" onClick={() => setShowFilters(false)}>
                    <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '95%' }}>
                        <div className="modal-header">
                            <h2>Filters</h2>
                            <button className="modal-close-btn" onClick={() => setShowFilters(false)}>✕</button>
                        </div>
                        <div className="modal-content" style={{ padding: '1.5rem' }}>
                            <div className="filter-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div className="filter-group">
                                    <label style={{ fontWeight: '600', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Location:</label>
                                    <select style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', outline: 'none' }}
                                        value={filters.location_id} onChange={(e) => setFilters({...filters, location_id: e.target.value})}>
                                        <option value="all">All Locations</option>
                                        {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label style={{ fontWeight: '600', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Adjustment Type:</label>
                                    <select style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', outline: 'none' }}
                                        value={filters.adjustment_type} onChange={(e) => setFilters({...filters, adjustment_type: e.target.value})}>
                                        <option value="all">All Types</option>
                                        <option value="normal">Normal</option>
                                        <option value="abnormal">Abnormal</option>
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label style={{ fontWeight: '600', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Start Date:</label>
                                    <input type="date" style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', outline: 'none' }}
                                        value={filters.start_date} onChange={(e) => setFilters({...filters, start_date: e.target.value})} />
                                </div>
                                <div className="filter-group">
                                    <label style={{ fontWeight: '600', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>End Date:</label>
                                    <input type="date" style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', outline: 'none' }}
                                        value={filters.end_date} onChange={(e) => setFilters({...filters, end_date: e.target.value})} />
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0' }}>
                            <button onClick={handleResetFilters}
                                style={{ padding: '0.6rem 1.25rem', border: '1px solid #e2e8f0', background: '#fff', borderRadius: '8px', cursor: 'pointer' }}>Reset</button>
                            <button onClick={handleApplyFilters}
                                style={{ padding: '0.6rem 1.25rem', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Apply Filters</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StockAdjustment;
